import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

export type UiMode = 'classic' | 'new';

export const UI_MODE_STORAGE_KEY = 'newstrack.ui-mode';
export const UI_MODE_COACHMARK_DISMISSED_STORAGE_KEY = 'newstrack.ui-mode-coachmark-dismissed';

interface IUiModeContext {
  uiMode: UiMode;
  setUiMode: (uiMode: UiMode) => void;
}

const defaultContext: IUiModeContext = {
  uiMode: 'classic',
  setUiMode: () => undefined,
};

const UiModeContext = createContext<IUiModeContext>(defaultContext);

function getStoredMode(): UiMode | null {
  const storedMode = window.localStorage.getItem(UI_MODE_STORAGE_KEY);
  return storedMode === 'classic' || storedMode === 'new' ? storedMode : null;
}

export function UiModeProvider({ children }: { children: ReactNode }) {
  const [uiMode, updateUiMode] = useState<UiMode>(() => getStoredMode() ?? 'classic');

  const context = useMemo<IUiModeContext>(
    () => ({
      uiMode,
      setUiMode: (nextUiMode: UiMode) => {
        if (nextUiMode === uiMode) return;

        window.localStorage.setItem(UI_MODE_STORAGE_KEY, nextUiMode);
        updateUiMode(nextUiMode);
        window.location.reload();
      },
    }),
    [uiMode]
  );

  return <UiModeContext.Provider value={context}>{children}</UiModeContext.Provider>;
}

export function useUiMode() {
  return useContext(UiModeContext);
}
